import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Blocks, FlaskConical, Car, Baby, Dog, Drama, Dices, Palette, Sun, Bike, Gamepad2, Sparkles, ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useStore } from "../context/StoreContext";

const NAVY = "#1B1F5C";

const CATEGORIES = [
  { id: 1, title: "Construction & Blocks", icon: Blocks, color: "#35C3C1", subs: [
    { label: "LEGO-Style Sets", q: "building set" }, { label: "Magnetic Blocks", q: "magnetic" },
    { label: "Advanced Building Sets", q: "building" }, { label: "Stacking Blocks", q: "stacking" },
  ]},
  { id: 2, title: "STEM & Educational", icon: FlaskConical, color: "#7C4DC4", subs: [
    { label: "STEM Kits", q: "stem" }, { label: "Robotics Kits", q: "robo" },
    { label: "Science Kits", q: "science" }, { label: "Educational Toys", q: "learn" },
  ]},
  { id: 3, title: "Vehicles & Remote Control", icon: Car, color: "#F0426A", subs: [
    { label: "RC Cars", q: "rc car" }, { label: "Remote-Control Vehicles", q: "remote" },
    { label: "Drones", q: "drone" }, { label: "Swing Cars", q: "swing" },
  ]},
  { id: 4, title: "Infant Care & Mobility", icon: Baby, color: "#F79433", subs: [
    { label: "Cradles", q: "cradle" }, { label: "Walkers", q: "walker" },
    { label: "Activity Mats", q: "activity" }, { label: "Teething Toys", q: "teething" }, { label: "Rattles", q: "rattle" },
  ]},
  { id: 5, title: "Plush & Sensory Toys", icon: Dog, color: "#4CAF50", subs: [
    { label: "Soft Toys", q: "soft toy" }, { label: "Sensory Toys", q: "sensory" }, { label: "Shape Sorters", q: "shape sorter" },
  ]},
  { id: 6, title: "Pretend Play & Roleplay", icon: Drama, color: "#35C3C1", subs: [
    { label: "Pretend-Play Sets", q: "pretend" }, { label: "Action Figures", q: "action figure" },
    { label: "Dolls & Accessories", q: "doll" }, { label: "Barbie Sets", q: "barbie" },
  ]},
  { id: 7, title: "Board Games & Puzzles", icon: Dices, color: "#7C4DC4", subs: [
    { label: "Board Games", q: "board game" }, { label: "Card Games", q: "game" },
    { label: "Strategy Games", q: "strategy" }, { label: "Puzzles", q: "puzzle" },
  ]},
  { id: 8, title: "Arts, Crafts & Hobbies", icon: Palette, color: "#F0426A", subs: [
    { label: "Arts & Crafts", q: "craft" }, { label: "Creative Hobby Kits", q: "hobby" }, { label: "Flash Cards", q: "flash card" },
  ]},
  { id: 9, title: "Outdoor & Heavy Play", icon: Sun, color: "#F79433", subs: [
    { label: "Outdoor Sports Sets", q: "outdoor" }, { label: "Cricket & Football", q: "cricket" },
    { label: "Slides", q: "slide" }, { label: "Tent Houses", q: "tent" },
  ]},
  { id: 10, title: "Ride-Ons & Trikes", icon: Bike, color: "#4CAF50", subs: [
    { label: "Ride-On Cars", q: "ride-on" }, { label: "Tricycles", q: "tricycle" }, { label: "Hopping Scooters", q: "hopping" },
  ]},
  { id: 11, title: "Electronics & Digital", icon: Gamepad2, color: "#35C3C1", subs: [
    { label: "Video Games", q: "video game" }, { label: "Interactive Digital Toys", q: "interactive" },
  ]},
  { id: 12, title: "Novelties & Collectibles", icon: Sparkles, color: "#7C4DC4", subs: [
    { label: "Pop-Culture Collectibles", q: "collectible" }, { label: "Figures & Blind Boxes", q: "figures" }, { label: "Limited Editions", q: "collectible" },
  ]},
];

export default function Categories() {
  const { wishlist, cart } = useStore();
  const navigate = useNavigate();

  const goSearch = (q) => navigate(`/search?q=${encodeURIComponent(q)}`);

  return (
    <div className="bg-white min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 pt-4 text-left">
          <p className="text-[12px] font-bold" style={{ color: NAVY }}>
            <Link to="/" className="hover:text-[#35C3C1] transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <span className="text-gray-500">All Categories</span>
          </p>
        </div>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 mt-4">
          <div className="rounded-3xl px-6 md:px-10 py-8 md:py-10 relative overflow-hidden text-left" style={{ backgroundColor: "#FBF3E4" }}>
            <svg className="absolute top-5 right-8 w-6 h-6 text-[#F79433]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <svg className="absolute bottom-5 right-24 w-4 h-4 text-[#35C3C1]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl" style={{ color: NAVY }} data-testid="categories-page-title">
              Explore All <span className="text-[#35C3C1]">Categories</span>
            </h1>
            <p className="mt-2 text-[13.5px] font-semibold text-[#5A5A75] max-w-xl leading-relaxed">
              From first rattles to robotics kits — browse our 12 toy worlds and tap any collection to see matching toys.
            </p>
          </div>
        </section>

        {/* Category grid */}
        <section className="max-w-7xl mx-auto px-4 mt-8 mb-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.id}
                data-testid={`category-card-${c.id}`}
                className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-transform duration-200 p-6 text-left flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <span className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${c.color}1A` }}>
                    <Icon size={22} style={{ color: c.color }} strokeWidth={1.8} />
                  </span>
                  <h2 className="font-display font-extrabold text-[17px] leading-tight" style={{ color: NAVY }}>{c.title}</h2>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 flex-1 content-start">
                  {c.subs.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => goSearch(s.q)}
                      data-testid={`subcat-${c.id}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="px-3.5 h-8 rounded-full bg-[#F7F7FA] text-[11.5px] font-extrabold hover:text-white transition-colors inline-flex items-center gap-1"
                      style={{ color: NAVY }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = c.color, e.currentTarget.style.color = "#fff")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#F7F7FA", e.currentTarget.style.color = NAVY)}
                    >
                      {s.label} <ChevronRight size={12} />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      </main>
      <Footer />
    </div>
  );
}
